<template>
  <ClientOnly>
    <div
      class="story-viewer-container"
      :style="storyViewerStyle"
    >
      <div class="md:max-w-xl md:ml-auto md:mr-auto md:relative flex flex-col items-center h-full">
        <!-- Progress bars -->
        <div class="flex space-x-1 p-2  w-full">
          <div
            v-for="(_, idx) in currentSlides"
            :key="idx"
            class="flex-1 h-1 bg-gray-600 overflow-hidden rounded"
          >
            <div
              class="h-full bg-white"
              :style="{ width: progressBars[idx] + '%' }"
            ></div>
          </div>
        </div>
        <!-- Close + Sound buttons -->
        <div class="btn__toolbar" :style="{ marginTop: `${safeTop + 26}px` }">
          <!-- Кнопка звука (только для видео-слайдов) -->
          <Transition name="fade">
            <UButton
              v-if="isCurrentSlideVideo"
              color="gray"
              variant="ghost"
              :icon="isMuted ? 'i-heroicons-speaker-x-mark' : 'i-heroicons-speaker-wave'"
              class="btn__sound"
              size="md"
              @click.stop="toggleMute"
            />
          </Transition>
          <UButton
            :ui="{
              icon: {
                base: 'flex-shrink-0 font-semibold',
                size: {
                  '2xs': 'h-4 w-4',
                  xs: 'h-4 w-4',
                  sm: 'h-5 w-5',
                  md: 'h-5 w-5',
                  lg: 'h-5 w-5',
                  xl: 'h-7 w-7',
                },
              },
            }"
            color="gray"
            variant="ghost"
            icon="i-heroicons-x-mark-16-solid"
            class="btn__close"
            size="md"
            @click="close"
          />
        </div>

        <!-- Жесты: тап (половина экрана = пред/след), удержание = пауза, свайп = листание.
             Единый слой управления — поэтому у swiper отключён свой touch (allow-touch-move=false),
             иначе он перехватывает касания и удержание/свайп работают некорректно. -->
        <div
          class="relative w-full flex-1 mb-6 touch-none select-none"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerCancel"
        >
          <!-- Оверлей паузы -->
          <Transition name="fade">
            <div v-if="isPaused" class="pause-overlay">
              <UIcon name="i-heroicons-pause-circle" class="w-16 h-16 text-white opacity-80" />
            </div>
          </Transition>

          <swiper-container
            ref="containerRef"
            class="w-full h-full"
            :loop="false"
            allow-touch-move="false"
            @slide-change="handleSlideChange"
          >
            <swiper-slide
              v-for="(slide, idx) in currentSlides"
              :key="slide.id"
              class="flex flex-col items-center justify-center"
            >
              <!-- Видео-слайд -->
              <video
                v-if="slide.video_url"
                :ref="(el: any) => setVideoRef(el, idx)"
                :src="slide.video_url"
                class="max-w-full max-h-[90vh] object-contain"
                playsinline
                @timeupdate="handleVideoTimeUpdate(idx)"
                @ended="handleVideoEnded"
              />
              <!-- Изображение-слайд -->
              <img
                v-else-if="slide.image_url"
                :src="slide.image_url"
                class="max-w-full max-h-[90vh] object-contain"
                :alt="slide.title || 'Story image'"
              />

              <!-- Заголовок и описание -->
              <div v-if="slide.title || slide.description" class="story-info">
                <h3 v-if="slide.title" class="story-title">{{ slide.title }}</h3>
                <p v-if="slide.description" class="story-description">{{ slide.description }}</p>
              </div>
            </swiper-slide>
          </swiper-container>
        </div>

        <!-- Кнопка действия (показывается только если есть действие) -->
        <div v-if="currentAction" class="CTA">
          <UiButtonMain
            :label=" currentAction.label"
            color="red"
            size="xl"
            block
            @click="handleActionClick"
          />
        </div>


      </div>
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { consola } from 'consola'
import apiNewsSlider, { parseSlideAction, expandSlideToViewSlides } from '~/entities/news-slider/api'
import { useSlideAction } from '@entities/news-slider'
import { useSafeArea, lockBodyScroll, unlockBodyScroll } from '@shared/lib'
import type { SlideGroup, NewsSliderV2 } from '~/entities/news-slider/api'

const props = defineProps<{
  slideGroups: SlideGroup[]
  initialGroupIndex: number
  delay?: number
}>()
const emit = defineEmits(['close'] as const)

const logger = consola.withTag('StoryViewer')

// Композабл для обработки действий
const { executeAction } = useSlideAction()

// Safe area для корректного отображения на iOS/Android
const { safeTop, safeBottom } = useSafeArea()

// Dynamic style с учетом safe area + fallback через CSS env()
const storyViewerStyle = computed(() => ({
  paddingTop: `${Math.max(safeTop.value, 16)}px`,    // Минимум 16px
  paddingBottom: `${Math.max(safeBottom.value, 80)}px`,  // Для CTA кнопки + навигация
}))

// Трекаем просмотры слайдов (чтобы не отправлять дубли)
const viewedSlides = new Set<string>()

// Контейнер для swiper
const containerRef = ref<any>(null)

// Свайпер-инстанс из web component
const getSwiperInstance = () => containerRef.value?.swiper
const slideTo = (index: number, speed = 300) => getSwiperInstance()?.slideTo(index, speed)
const nextSlide = () => getSwiperInstance()?.slideNext()

// ─── Video support ─────────────────────────────────────────────────────────

// Хранит ссылки на <video> элементы по индексу слайда
const videoRefs: Record<number, HTMLVideoElement> = {}

function setVideoRef(el: any, idx: number) {
  if (el) videoRefs[idx] = el as HTMLVideoElement
  else delete videoRefs[idx]
}

const isCurrentSlideVideo = computed(() => !!currentSlide.value?.video_url)

// Звук: по умолчанию muted — нужен для автоплея без взаимодействия
const isMuted = ref(true)

function toggleMute() {
  isMuted.value = !isMuted.value
  Object.values(videoRefs).forEach(v => { v.muted = isMuted.value })
}

watch(isMuted, (val) => {
  Object.values(videoRefs).forEach(v => { v.muted = val })
})

function pauseAllVideos() {
  Object.values(videoRefs).forEach(v => { if (!v.paused) v.pause() })
}

function handleVideoTimeUpdate(idx: number) {
  if (idx !== currentSlideIndex.value) return
  const video = videoRefs[idx]
  if (!video?.duration) return
  progressBars.value[idx] = Math.min((video.currentTime / video.duration) * 100, 100)
}

function handleVideoEnded() {
  progressBars.value[currentSlideIndex.value] = 100
  if (currentSlideIndex.value < currentSlides.value.length - 1) {
    const newIdx = currentSlideIndex.value + 1
    pauseAllVideos()
    currentSlideIndex.value = newIdx
    progressBars.value = currentSlides.value.map((_, i) => (i < newIdx ? 100 : 0))
    slideTo(newIdx, 300)
    startProgress()
  } else {
    goToNextGroup()
  }
}

// ─── Жесты: тап / удержание-пауза / свайп ────────────────────────────────────

const isPaused = ref(false)

// Пороги распознавания жеста
const HOLD_DELAY = 180        // мс до срабатывания паузы при удержании
const MOVE_TOLERANCE = 10     // px: в пределах — считаем «без движения» (тап/удержание)
const SWIPE_THRESHOLD = 40    // px горизонтали — это свайп (листание)

let holdTimer: ReturnType<typeof setTimeout> | null = null
let gestureStartX = 0
let gestureStartY = 0
let gestureMoved = false      // было ли движение (тогда не тап)
let gestureSwiped = false     // распознан горизонтальный свайп
// Прогресс в момент паузы (для возобновления таймерного слайда с той же точки)
let pausedAtProgress = 0

function holdPause() {
  isPaused.value = true
  if (isCurrentSlideVideo.value) {
    videoRefs[currentSlideIndex.value]?.pause()
  } else {
    if (timer) { clearInterval(timer); timer = null }
    pausedAtProgress = progressBars.value[currentSlideIndex.value] ?? 0
  }
}

function holdResume() {
  isPaused.value = false
  if (isCurrentSlideVideo.value) {
    videoRefs[currentSlideIndex.value]?.play().catch(() => {})
  } else {
    // Возобновляем с сохранённой точки прогресса
    const startPct = pausedAtProgress
    const remaining = duration.value * (1 - startPct / 100)
    const startTime = Date.now()
    const idx = currentSlideIndex.value
    if (timer) { clearInterval(timer); timer = null }
    timer = window.setInterval(() => {
      const pct = Math.min(startPct + (Date.now() - startTime) / remaining * (100 - startPct), 100)
      progressBars.value[idx] = pct
      if (pct >= 100) {
        clearInterval(timer!); timer = null
        if (idx < currentSlides.value.length - 1) nextSlide()
        else goToNextGroup()
      }
    }, 50)
  }
}

function clearHoldTimer() {
  if (holdTimer) { clearTimeout(holdTimer); holdTimer = null }
}

function onPointerDown(e: PointerEvent) {
  // Игнорируем не-основную кнопку мыши
  if (e.pointerType === 'mouse' && e.button !== 0) return
  gestureStartX = e.clientX
  gestureStartY = e.clientY
  gestureMoved = false
  gestureSwiped = false
  // Захватываем указатель: дальнейшие move/up придут сюда даже если палец «уехал»
  // за пределы элемента — без этого удержание спонтанно срывалось (pointerleave/cancel).
  try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* не поддерживается */ }
  // Пауза по удержанию — слайд на паузе, ПОКА палец на экране.
  holdTimer = setTimeout(() => {
    holdTimer = null
    holdPause()
  }, HOLD_DELAY)
}

function onPointerMove(e: PointerEvent) {
  const dx = e.clientX - gestureStartX
  const dy = e.clientY - gestureStartY
  // Любое заметное движение — это уже не тап и не удержание: снимаем таймер паузы.
  if (!gestureMoved && (Math.abs(dx) > MOVE_TOLERANCE || Math.abs(dy) > MOVE_TOLERANCE)) {
    gestureMoved = true
    clearHoldTimer()
  }
  // Горизонтальное движение с преобладанием по X — свайп-листание.
  if (!gestureSwiped && Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
    gestureSwiped = true
  }
}

function onPointerUp(e: PointerEvent) {
  clearHoldTimer()
  try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* no-op */ }

  // 1) Было удержание-пауза → просто снимаем паузу, без навигации.
  if (isPaused.value) { holdResume(); return }

  // 2) Горизонтальный свайп → влево = следующий, вправо = предыдущий.
  if (gestureSwiped) {
    if (e.clientX - gestureStartX < 0) handleNextSlide()
    else handlePrevSlide()
    return
  }

  // 3) Тап без движения → по половине экрана: левая = пред, правая = след.
  if (!gestureMoved) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    if (e.clientX - rect.left < rect.width / 2) handlePrevSlide()
    else handleNextSlide()
  }
}

function onPointerCancel() {
  clearHoldTimer()
  // Спонтанный cancel при захвате указателя + touch-action:none крайне маловероятен,
  // но если случился во время паузы — снимаем её, чтобы не «зависнуть» на паузе.
  if (isPaused.value) holdResume()
}

// ───────────────────────────────────────────────────────────────────────────

// Текущий индекс группы историй
const currentGroupIndex = ref(props.initialGroupIndex)

// Текущая группа слайдов
const currentGroup = computed(() => props.slideGroups[currentGroupIndex.value])

// Слайды текущей группы (развернутые с учетом internal_slides)
const currentSlides = computed<NewsSliderV2[]>(() => {
  const groupSlides = currentGroup.value?.slides ?? []

  // Разворачиваем каждый слайд с internal_slides в массив слайдов
  const expandedSlides: NewsSliderV2[] = []

  for (const slide of groupSlides) {
    const viewSlides = expandSlideToViewSlides(slide)
    expandedSlides.push(...viewSlides)
  }

  return expandedSlides
})

// Текущий индекс слайда внутри группы
const currentSlideIndex = ref(0)

// Текущий слайд
const currentSlide = computed(() => currentSlides.value[currentSlideIndex.value])

// Действие для текущего слайда
const currentAction = computed(() => {
  if (!currentSlide.value) return null

  // Показываем кнопку на любом слайде, если у него есть действие
  return parseSlideAction(currentSlide.value)
})

// Таймаут между слайдами, ms
const duration = computed(() => props.delay ?? 8000)

// Прогресс-бары для каждого слайда в текущей группе
const progressBars = ref<number[]>([])

let timer: number | null = null

// Инициализация массива progressBars при смене группы
watch(
  currentSlides,
  (slides) => {
    pauseAllVideos()
    progressBars.value = slides.map(() => 0)
    currentSlideIndex.value = 0
  },
  { immediate: true },
)

// Отправляем view при показе слайда (только для основных слайдов, не для виртуальных внутренних).
//
// immediate обязателен: currentSlide уже имеет значение к моменту монтирования, и вотчер
// без него срабатывал только на ПЕРЕКЛЮЧЕНИИ. Первый слайд группы не считался никогда, а у
// одиночного слайда (группа из одного) показов не появлялось вовсе — при живых кликах это
// задирало CTR.
watch(
  currentSlide,
  (slide) => {
    if (slide && !viewedSlides.has(slide.id)) {
      viewedSlides.add(slide.id)
      // Отправляем трекинг только если это не виртуальный внутренний слайд
      if (!slide.id.includes('_internal_')) {
        apiNewsSlider.trackView(slide.id)
      }
    }
  },
  { immediate: true },
)

// Переход к следующей группе историй
function goToNextGroup() {
  if (currentGroupIndex.value < props.slideGroups.length - 1) {
    if (timer) { clearInterval(timer); timer = null }
    pauseAllVideos()
    currentGroupIndex.value++
    // Ждём nextTick — Vue успеет обновить currentSlides и DOM
    nextTick(() => {
      currentSlideIndex.value = 0
      progressBars.value = currentSlides.value.map(() => 0)
      slideTo(0, 0)
      startProgress()
    })
  } else {
    close()
  }
}

// Переход к предыдущей группе историй
function goToPrevGroup() {
  if (currentGroupIndex.value > 0) {
    if (timer) { clearInterval(timer); timer = null }
    pauseAllVideos()
    currentGroupIndex.value--
    nextTick(() => {
      const targetIdx = currentSlides.value.length - 1
      currentSlideIndex.value = targetIdx
      progressBars.value = currentSlides.value.map((_, i) => (i < targetIdx ? 100 : 0))
      slideTo(targetIdx, 0)
      startProgress()
    })
  }
}

// Таймерный прогресс для слайдов-изображений
function startTimerProgress() {
  if (timer) { clearInterval(timer); timer = null }
  const idx = currentSlideIndex.value
  const startTime = Date.now()
  timer = window.setInterval(() => {
    const pct = Math.min(((Date.now() - startTime) / duration.value) * 100, 100)
    progressBars.value[idx] = pct
    if (pct >= 100) {
      clearInterval(timer!); timer = null
      if (idx < currentSlides.value.length - 1) {
        // Переходим к следующему слайду напрямую, без next()
        pauseAllVideos()
        const newIdx = idx + 1
        currentSlideIndex.value = newIdx
        progressBars.value = currentSlides.value.map((_, i) => (i < newIdx ? 100 : 0))
        slideTo(newIdx, 300)
        startProgress()
      } else {
        goToNextGroup()
      }
    }
  }, 50)
}

// Запускаем/перезапускаем прогресс для текущего слайда
function startProgress() {
  if (timer) { clearInterval(timer); timer = null }

  if (isCurrentSlideVideo.value) {
    const playVideo = () => {
      const video = videoRefs[currentSlideIndex.value]
      if (video) {
        video.muted = isMuted.value
        video.currentTime = 0
        video.play().catch(() => startTimerProgress())
      } else {
        // ref ещё не установлен (DOM не обновился) — пробуем на следующем тике
        nextTick(() => {
          const v = videoRefs[currentSlideIndex.value]
          if (v) {
            v.muted = isMuted.value
            v.currentTime = 0
            v.play().catch(() => startTimerProgress())
          } else {
            startTimerProgress()
          }
        })
      }
    }
    playVideo()
    return
  }

  startTimerProgress()
}

// Обработка @slide-change от swiper-container (только для ручного свайпа пальцем)
// Swiper web component передаёт swiper-инстанс в event.detail[0]
function handleSlideChange(event: any) {
  const detail = event?.detail?.[0]
  const sw = detail?.swiper ?? detail ?? getSwiperInstance()
  const real: number = sw?.realIndex ?? sw?.activeIndex ?? 0
  // Пропускаем, если индекс уже актуален (slideTo вызван нами)
  if (real === currentSlideIndex.value) return
  pauseAllVideos()
  if (timer) { clearInterval(timer); timer = null }
  currentSlideIndex.value = real
  progressBars.value = currentSlides.value.map((_, i) => (i < real ? 100 : 0))
  startProgress()
}

// Следующий слайд (тап по правой половине / свайп влево)
function handleNextSlide() {
  if (currentSlideIndex.value < currentSlides.value.length - 1) {
    if (timer) { clearInterval(timer); timer = null }
    pauseAllVideos()
    const newIdx = currentSlideIndex.value + 1
    currentSlideIndex.value = newIdx
    progressBars.value = currentSlides.value.map((_, i) => (i < newIdx ? 100 : 0))
    slideTo(newIdx, 300)
    startProgress()
  } else {
    goToNextGroup()
  }
}

// Предыдущий слайд (тап по левой половине / свайп вправо)
function handlePrevSlide() {
  if (currentSlideIndex.value > 0) {
    if (timer) { clearInterval(timer); timer = null }
    pauseAllVideos()
    const newIdx = currentSlideIndex.value - 1
    currentSlideIndex.value = newIdx
    progressBars.value = currentSlides.value.map((_, i) => (i < newIdx ? 100 : 0))
    slideTo(newIdx, 300)
    startProgress()
  } else {
    goToPrevGroup()
  }
}

// Закрытие
function close() {
  if (timer) clearInterval(timer)
  pauseAllVideos()
  emit('close')
}

// Обработка клика на кнопку действия
const handleActionClick = async () => {
  if (!currentAction.value || !currentSlide.value) return

  // Останавливаем таймер и видео на время выполнения действия
  if (timer) clearInterval(timer)
  pauseAllVideos()

  // Отправляем событие клика для аналитики (только для основных слайдов)
  if (!currentSlide.value.id.includes('_internal_')) {
    apiNewsSlider.trackClick(currentSlide.value.id)
  }

  try {
    await executeAction(currentAction.value)

    // Умная логика: закрываем или продолжаем в зависимости от типа действия
    const shouldClose = shouldCloseAfterAction(currentAction.value.type)

    if (shouldClose) {
      close()
    } else if (currentSlideIndex.value < currentSlides.value.length - 1) {
        const newIdx = currentSlideIndex.value + 1
        currentSlideIndex.value = newIdx
        progressBars.value = currentSlides.value.map((_, i) => (i < newIdx ? 100 : 0))
        slideTo(newIdx, 300)
        startProgress()
      } else {
        goToNextGroup()
      }
  } catch (error) {
    logger.error('[StoryViewer] Error executing action:', error)
    // В случае ошибки продолжаем показ
    startProgress()
  }
}

// Определяем, нужно ли закрывать просмотрщик после действия
const shouldCloseAfterAction = (actionType: string): boolean => {
  switch (actionType) {
    case 'open_product': {
      // Открыли товар - закрываем просмотрщик
      return true
    }

    case 'navigate': {
      // Переход на другую страницу - закрываем
      return true
    }

    case 'copy_promo':
    case 'external_link':
    case 'share_referral': {
      // Эти действия не мешают просмотру - продолжаем
      return false
    }

    default: {
      return false
    }
  }
}

// Запускаем воспроизведение первого слайда после инициализации swiper.
// Сторис — фуллскрин-оверлей: лочим скролл фона на всё время показа
// (с компенсацией скроллбара, чтобы фон не «прыгал»).
onMounted(() => {
  lockBodyScroll()
  nextTick(() => {
    const sw = getSwiperInstance()
    if (!sw) return
    sw.slideTo(0, 0)
    startProgress()
  })
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
  pauseAllVideos()
  unlockBodyScroll()
})
</script>

<style scoped lang="scss">
.story-viewer-container {
  @apply fixed inset-0 px-0 bg-black bg-opacity-80 z-50;

  // CSS fallback для safe area (работает даже если JS не загрузился)
  padding-top: env(safe-area-inset-top, 1rem);
  padding-bottom: env(safe-area-inset-bottom, 5rem);

  // Fallback для старых iOS версий
  @supports (padding: constant(safe-area-inset-top)) {
    padding-top: constant(safe-area-inset-top);
    padding-bottom: constant(safe-area-inset-bottom);
  }
}

.btn__toolbar {
  @apply absolute right-1 lg:right-2 z-50 flex items-center gap-1;
}

.btn__close {
  @apply bg-white text-black rounded-full hover:rotate-90;
}

.btn__sound {
  @apply bg-white bg-opacity-90 text-black rounded-full;
}

.pause-overlay {
  @apply absolute inset-0 z-20 flex items-center justify-center pointer-events-none;
  background: rgba(0, 0, 0, 0.25);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Анимация заполнения прогресс-баров */
div[class*="flex"] > div > div {
  transition: width 0.1s linear;
}

.CTA {
  @apply absolute z-10 px-4 w-full;
  // Убрали fixed bottom-3, теперь bottom определяется padding-bottom родителя
  bottom: 0;
}

.action-button {
  @apply w-full py-4 px-6 rounded-2xl font-semibold text-lg
         bg-gradient-to-r from-red-500 to-red-600
         text-white shadow-lg
         flex items-center justify-center gap-3
         transition-all duration-200
         active:scale-95 hover:shadow-xl;
}

.action-icon {
  @apply text-2xl;
  animation: bounce-subtle 2s ease-in-out infinite;
}

.action-label {
  @apply font-bold;
}

@keyframes bounce-subtle {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

.story-info {
  @apply mt-4 text-center px-4 max-w-md;
}

.story-title {
  @apply text-white text-lg font-semibold mb-2;
}

.story-description {
  @apply text-white text-sm opacity-90;
}
</style>
